import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { useSelector } from "react-redux";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import CustomDropdown from "../../../custom/customDropdown";
import { AuthContext } from "../../../Header/auth - context";
import { uzmiGresku } from "../../../custom/greske";
import "../Fakture.css";

/* Datum se šalje kroz toUtcIso — bez korekcije offseta toISOString ume da
   pomeri datum za dan unazad. */
const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

/* Prvi dan tekućeg meseca — najčešći period koji direktor traži. */
const pocetakMeseca = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
};

const iznos = (v) =>
    v === null || v === undefined
        ? ""
        : Number(v).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PregledFaktura = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const toast = useRef(null);
    const company = useSelector((s) => s.company);
    /* Id prijavljenog korisnika — po njemu se odlucuje da li se vidi dugme
       „Potvrdi proveru“. Server proverava isto, nezavisno. */
    const korisnikId = useSelector((s) => s.user?.userId);

    const [sifarnici, setSifarnici] = useState({});
    const [podaci, setPodaci] = useState([]);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [ucitava, setUcitava] = useState(false);
    const [sinhronizuje, setSinhronizuje] = useState(false);

    const [datumOd, setDatumOd] = useState(pocetakMeseca());
    const [datumDo, setDatumDo] = useState(new Date());
    const [statusFilter, setStatusFilter] = useState(null);
    const [pibFilter, setPibFilter] = useState(null);

    const [izabrani, setIzabrani] = useState(null);

    /* Potvrda odluke — namerno NIJE generičko "Da li ste sigurni?".
       Odluka je poreska i SEF je ne dozvoljava da se promeni, pa u pitanju mora
       da stoji baš taj dokument, taj dobavljač i taj iznos. */
    const [odluka, setOdluka] = useState(null); // { prihvacen: bool }
    const [razlog, setRazlog] = useState("");
    const [salje, setSalje] = useState(false);

    /* { spisak: [{redni, naziv, mime, velicinaKb}], ucitava } */
    const [priloziDialog, setPriloziDialog] = useState(null);

    /* dodela na proveru: { id_zaduzen, napomena } | null */
    const [dodela, setDodela] = useState(null);
    /* potvrda provere: { napomena } | null */
    const [provera, setProvera] = useState(null);
    /* { url, naziv } — blob: adresa PDF-a i ime pod kojim se preuzima */
    const [pdfDialog, setPdfDialog] = useState(null);

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 5000 });

    const zaglavlja = () => ({
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
    });

    const posalji = async (putanja, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/sef/" + putanja, {
            method: "POST",
            headers: zaglavlja(),
            body: JSON.stringify(telo),
        });
        const tekst = await r.text();
        const podaci = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(podaci.message || t("common.greska"));
        return podaci;
    };

    const greska = async (e) => {
        const g = await uzmiGresku(e, t("common.greska"));
        poruka("error", t("common.greska"), g.message);
    };

    useEffect(() => {
        posalji("UlazneInit").then(setSifarnici).catch(greska);
        // eslint-disable-next-line
    }, []);

    const ucitaj = () => {
        setUcitava(true);
        posalji("UlaznePregled", {
            datumOd: toUtcIso(datumOd),
            datumDo: toUtcIso(datumDo),
            statusUlazne: statusFilter,
            supplierVat: pibFilter,
        })
            .then((d) => { setPodaci(d || []); setIzabrani(null); })
            .catch(greska)
            .finally(() => setUcitava(false));
    };

    useEffect(() => { ucitaj(); /* eslint-disable-next-line */ }, []);

    /* Neko mi je upravo dodelio fakturu na proveru.

       Dogadjaj stize sa socket-a kroz ChatSocketContext, koji ga prosledjuje kao
       dogadjaj prozora — kontekst je globalan i nema svoj Toast, a dodela treba da
       se vidi sa bilo kog ekrana. Ovde se, uz poruku, i spisak osvezava: korisnik
       koji bas gleda ovaj ekran inace ne bi video novu fakturu dok ne klikne
       "Ucitaj". */
    useEffect(() => {
        const naDodelu = (e) => {
            const d = e?.detail || {};
            poruka(
                "info",
                t("sefUlazne.novaZaProveru"),
                [d.PosiljalacIme, d.SupplierName, d.DocumentNumber].filter(Boolean).join(" · "),
            );
            ucitaj();
        };
        window.addEventListener("tms:ulazna-dodeljena", naDodelu);
        return () => window.removeEventListener("tms:ulazna-dodeljena", naDodelu);
        // eslint-disable-next-line
    }, []);

    /* "Osveži sa SEF-a" povlači SAMO spisak, ne i XML-ove — preuzimanje XML-a
       ide tek na klik "Original", jer nije potvrđeno da ono ne pokreće rok. */
    const sinhronizuj = () => {
        setSinhronizuje(true);
        posalji("UlazneSync", { datumOd: toUtcIso(datumOd), datumDo: toUtcIso(datumDo) })
            .then((d) => {
                poruka("success", t("sefUlazne.sinhronizacija"),
                    d.poruka || t("sefUlazne.obradjeno", { broj: d.obradjeno }));
                ucitaj();
            })
            .catch(greska)
            .finally(() => setSinhronizuje(false));
    };

    const otvoriPriloge = () => {
        if (!izabrani) return;
        setPriloziDialog({ spisak: [], ucitava: true });
        posalji("UlazniPrilozi", { id: izabrani.id })
            .then((d) => setPriloziDialog({ spisak: d.prilozi || [], ucitava: false }))
            .catch((e) => { setPriloziDialog(null); greska(e); });
    };

    /* Prilog se otvara u novoj kartici; blob se oslobađa tek posle otvaranja,
       jer bi ga trenutno oslobađanje ostavilo praznom. */
    const otvoriPrilog = (p, preuzmi) => {
        posalji("UlazniPrilog", { id: izabrani.id, redni: p.redni })
            .then((d) => {
                const bajtovi = Uint8Array.from(atob(d.base64), (c) => c.charCodeAt(0));
                const url = URL.createObjectURL(new Blob([bajtovi], { type: d.mime || "application/octet-stream" }));
                const a = document.createElement("a");
                a.href = url;
                if (preuzmi) a.download = (d.naziv || "prilog").replace(/[\\/:*?"<>|]/g, "-");
                else { a.target = "_blank"; a.rel = "noopener"; }
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            })
            .catch(greska);
    };

    /* PDF stize kao base64 iz SEF envelope — pretvara se u blob, da se dokument
       otvori u okviru ekrana umesto da se korisnik salje na SEF portal. */
    const otvoriDokument = () => {
        if (!izabrani) return;
        posalji("UlazniPdf", { id: izabrani.id })
            .then((d) => {
                const bajtovi = Uint8Array.from(atob(d.pdfBase64), (c) => c.charCodeAt(0));
                const url = URL.createObjectURL(new Blob([bajtovi], { type: "application/pdf" }));
                setPdfDialog({
                    url,
                    naziv: `${(izabrani.DocumentNumber || "faktura").replace(/[\\/:*?"<>|]/g, "-")}.pdf`,
                });
            })
            .catch(greska);
    };

    /* Blob mora da se oslobodi, inace svaki otvoren dokument ostane u memoriji. */
    const zatvoriDokument = () => {
        if (pdfDialog?.url) URL.revokeObjectURL(pdfDialog.url);
        setPdfDialog(null);
    };

    const potvrdiDodelu = () => {
        if (!izabrani || !dodela?.id_zaduzen) {
            poruka("warn", t("common.upozorenje"), t("sefUlazne.izaberiOperatera"));
            return;
        }
        setSalje(true);
        posalji("UlazniDodeli", {
            id: izabrani.id,
            id_zaduzen: dodela.id_zaduzen,
            napomena: (dodela.napomena || "").trim() || null,
        })
            .then((d) => {
                poruka("success", t("sefUlazne.dodeli"), d.message);
                setDodela(null);
                ucitaj();
            })
            .catch(greska)
            .finally(() => setSalje(false));
    };

    /* Potvrda provere ujedno prihvata fakturu na SEF-u — zato ista potvrda kao
       kod odluke, sa istim upozorenjem da je nepovratna. */
    const potvrdiProveru = () => {
        if (!izabrani) return;
        setSalje(true);
        posalji("UlazniProvera", {
            id: izabrani.id,
            napomena: (provera?.napomena || "").trim() || null,
        })
            .then((d) => {
                poruka("success", t("sefUlazne.potvrdiProveru"), d.message);
                setProvera(null);
                ucitaj();
            })
            .catch(greska)
            .finally(() => setSalje(false));
    };

    const potvrdiOdluku = () => {
        if (!izabrani || !odluka) return;
        if (!odluka.prihvacen && !razlog.trim()) {
            poruka("warn", t("common.upozorenje"), t("sefUlazne.razlogObavezan"));
            return;
        }
        setSalje(true);
        posalji("UlazniOdluka", {
            id: izabrani.id,
            prihvacen: odluka.prihvacen,
            napomena: razlog.trim() || null,
        })
            .then((d) => {
                poruka("success", t("common.sacuvano"), d.message);
                setOdluka(null); setRazlog("");
                ucitaj();
            })
            .catch(greska)
            .finally(() => setSalje(false));
    };

    /* Prihvati/Odbij sme direktor I eskalirani korisnik — isti uslov koji
       server proverava kroz requireDirektorIliEskaliran. */
    const smeOdluku = sifarnici.SmeOdluku === 1;
    const cekaOdluku = izabrani && izabrani.StatusUlazneNaziv &&
        String(izabrani.StatusUlazneNaziv).toLowerCase().startsWith("čeka");

    const tipOznaka = (r) => {
        const mapa = {
            Invoice:    { txt: t("sefUlazne.tipFaktura"),    klasa: "sef-tip-faktura" },
            CreditNote: { txt: t("sefUlazne.tipOdobrenje"),  klasa: "sef-tip-odobrenje" },
            DebitNote:  { txt: t("sefUlazne.tipZaduzenje"),  klasa: "sef-tip-zaduzenje" },
            Prepayment: { txt: t("sefUlazne.tipAvans"),      klasa: "sef-tip-avans" },
        };
        const o = mapa[r.DocumentType] || { txt: r.DocumentType || "", klasa: "" };
        return <span className={`sef-pilula ${o.klasa}`}>{o.txt}</span>;
    };

    /* Dve kolone statusa, ne jedna: "SEF" je istina kod države, "Status" je naša
       odluka. Kad se raziđu, to je greška koju treba videti, a ne sakriti. */
    const sefOznaka = (r) => {
        const kritican = r.SefStatus === "ReNotified";
        return <span className={`sef-pilula ${kritican ? "sef-st-kritican" : "sef-st-info"}`}>{r.SefStatus || ""}</span>;
    };

    const statusOznaka = (r) => {
        if (!r.StatusUlazneNaziv) return "";
        const naziv = String(r.StatusUlazneNaziv).toLowerCase();
        let klasa = "sef-st-info";
        let dodatak = "";
        if (naziv.startsWith("čeka")) {
            klasa = r.RokIstekao ? "sef-st-kritican" : "sef-st-upozorenje";
            if (r.DanaCekanja != null) dodatak = ` · ${t("sefUlazne.dana", { broj: r.DanaCekanja })}`;
        } else if (naziv === "odobren") {
            klasa = "sef-st-ok";
            // Radna lista knjigovođe: direktor je rekao da, ostalo je da se unese.
            if (!r.id_faktura) dodatak = ` · ${t("sefUlazne.nijeKnjizen")}`;
        } else if (naziv === "odbijen" || naziv === "storniran") {
            klasa = "sef-st-kritican";
        }
        return <span className={`sef-pilula ${klasa}`}>{r.StatusUlazneNaziv}{dodatak}</span>;
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Ulazne SEF");
        const headers = [
            t("sefUlazne.broj"), t("sefUlazne.tip"), t("ulazne.dobavljac"), t("sefUlazne.pib"),
            t("ulazne.neto"), t("ulazne.pdv"), t("ulazne.ukupno"), t("ulazne.valuta"),
            t("sefUlazne.stiglo"), t("ulazne.rokPlacanja"), t("sefUlazne.sefStatus"),
            t("ulazne.status"), t("sefUlazne.danaCekanja"), t("ulazne.razlogOdbijanja"),
        ];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });
        (podaci || []).forEach((r) => {
            const row = worksheet.addRow([
                r.DocumentNumber || "", r.DocumentType || "", r.SupplierName || "", r.SupplierVat || "",
                r.SumWithoutVat, r.VatAmount, r.Amount, r.Currency || "",
                r.SentDate || "", r.DueDate || "", r.SefStatus || "",
                r.StatusUlazneNaziv || "", r.DanaCekanja ?? "", r.NapomenaOdluke || "",
            ]);
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });
        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString("sr-RS", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade",
        }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `Ulazne fakture SEF - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                    onClick={exportExcel} data-pr-tooltip="XLS" />
            </div>
            <input type="search" placeholder={t("nova.phPretraga")}
                className="p-inputtext p-component"
                onInput={(e) => setGlobalFilter(e.target.value)} />
        </div>
    );

    const naslovPotvrde = odluka?.prihvacen
        ? t("sefUlazne.potvrdaPrihvati")
        : t("sefUlazne.potvrdaOdbij");

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <div className="faktureStranica">

                <Card>
                    <div className="sef-ulazne-filter">
                        <div className="sef-filter-polje">
                            <label>{t("sefUlazne.periodOd")}:</label>
                            <Calendar value={datumOd} onChange={(e) => setDatumOd(e.value)}
                                dateFormat="dd.mm.yy." showIcon />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("sefUlazne.periodDo")}:</label>
                            <Calendar value={datumDo} onChange={(e) => setDatumDo(e.value)}
                                dateFormat="dd.mm.yy." showIcon />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("ulazne.status")}:</label>
                            <CustomDropdown value={statusFilter} optionLabel="label" showClear
                                options={sifarnici.StatusiDB || []}
                                placeholder={t("sefUlazne.sviStatusi")}
                                onChange={(e) => setStatusFilter(e.target.value)} />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("ulazne.dobavljac")}:</label>
                            <CustomDropdown value={pibFilter} optionLabel="label" showClear filter
                                options={sifarnici.DobavljaciDB || []}
                                placeholder={t("sefUlazne.sviDobavljaci")}
                                onChange={(e) => setPibFilter(e.target.value)} />
                        </div>
                        <div className="sef-filter-dugmad">
                            <Button type="button" label={t("sefUlazne.ucitaj")} icon="pi pi-search"
                                className="p-button-sm" loading={ucitava} onClick={ucitaj} />
                            <Button type="button" label={t("sefUlazne.osveziSaSefa")} icon="pi pi-cloud-download"
                                className="p-button-sm p-button-outlined" loading={sinhronizuje}
                                onClick={sinhronizuj} />
                        </div>
                    </div>
                </Card>

                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <DataTable
                        value={podaci || []}
                        paginator rows={25} rowsPerPageOptions={[25, 50, 100]}
                        globalFilter={globalFilter}
                        scrollable scrollHeight="50vh"
                        header={tableHeader}
                        loading={ucitava}
                        emptyMessage={t("common.noDataTable")}
                        resizableColumns columnResizeMode="expand"
                        className="turePregledTable stavke-tabela"
                        selectionMode="single" selection={izabrani}
                        onSelectionChange={(e) => setIzabrani(e.value)}
                        rowClassName={(r) => ({ "sef-red-istekao": r.RokIstekao === 1 })}>
                        <Column header={t("sefUlazne.broj")} style={{ minWidth: "170px" }}
                            body={(r) => (
                                <>
                                    {r.DocumentNumber}
                                    {r.VecUneta === 1 && (
                                        <span className="sef-pilula sef-st-upozorenje sef-vec-uneta"
                                            title={t("sefUlazne.vecUnetaOpis", { broj: r.PostojecaFakturaBroj })}>
                                            <i className="pi pi-exclamation-triangle" /> {t("sefUlazne.vecUneta")}
                                        </span>
                                    )}
                                </>
                            )} />
                        <Column header={t("sefUlazne.tip")} body={tipOznaka} style={{ minWidth: "110px" }} />
                        <Column field="SupplierName" header={t("ulazne.dobavljac")} style={{ minWidth: "190px" }} />
                        <Column field="SupplierVat" header={t("sefUlazne.pib")} style={{ minWidth: "100px" }} />
                        <Column header={t("ulazne.neto")} style={{ minWidth: "110px" }}
                            body={(r) => <span className="sef-iznos">{iznos(r.SumWithoutVat)}</span>} />
                        <Column header={t("ulazne.pdv")} style={{ minWidth: "100px" }}
                            body={(r) => <span className="sef-iznos">{iznos(r.VatAmount)}</span>} />
                        <Column header={t("ulazne.ukupno")} style={{ minWidth: "120px" }}
                            body={(r) => <strong className="sef-iznos">{iznos(r.Amount)}</strong>} />
                        <Column field="Currency" header={t("ulazne.valuta")} style={{ minWidth: "70px" }} />
                        <Column field="SentDate" header={t("sefUlazne.stiglo")} style={{ minWidth: "100px" }} />
                        <Column field="DueDate" header={t("ulazne.rokPlacanja")} style={{ minWidth: "100px" }} />
                        <Column header={t("sefUlazne.sefStatus")} body={sefOznaka} style={{ minWidth: "110px" }} />
                        <Column header={t("sefUlazne.zaduzen")} style={{ minWidth: "170px" }}
                            body={(r) => !r.ZaduzenIme ? "—" : (
                                <span className="sef-zaduzen" title={r.NapomenaZaduzenja || ""}>
                                    {r.ZaduzenIme}
                                    {r.ProverioIme && (
                                        <span className="sef-pilula sef-st-ok sef-provereno">
                                            <i className="pi pi-check" /> {t("sefUlazne.provereno")}
                                        </span>
                                    )}
                                </span>
                            )} />
                        <Column header={t("ulazne.status")} body={statusOznaka} style={{ minWidth: "190px" }} />
                    </DataTable>

                    <div className="sef-ulazne-akcije">
                        <span className="sef-izabrano">
                            {izabrani
                                ? `${t("sefUlazne.izabrano")}: ${izabrani.DocumentNumber || ""}`
                                : t("sefUlazne.nemaIzbora")}
                        </span>

                        {/* Backend proverava isto, nezavisno — ovo je samo da ostali
                            ne gledaju dugmad koja im ionako ne rade. */}
                        {smeOdluku && (
                            <>
                                <Button type="button" label={t("sefUlazne.prihvati")} icon="pi pi-check"
                                    severity="success" className="p-button-sm"
                                    disabled={!izabrani || !cekaOdluku}
                                    onClick={() => { setRazlog(""); setOdluka({ prihvacen: true }); }} />
                                <Button type="button" label={t("sefUlazne.odbij")} icon="pi pi-times"
                                    severity="danger" outlined className="p-button-sm"
                                    disabled={!izabrani || !cekaOdluku}
                                    onClick={() => { setRazlog(""); setOdluka({ prihvacen: false }); }} />
                            </>
                        )}

                        <Button type="button" label={t("sefUlazne.dokument")} icon="pi pi-file-pdf"
                            className="p-button-sm"
                            disabled={!izabrani} onClick={otvoriDokument} />
                        <Button type="button" label={t("sefUlazne.dodeli")} icon="pi pi-user-plus"
                            className="p-button-sm p-button-outlined"
                            disabled={!izabrani || !cekaOdluku}
                            onClick={() => setDodela({ id_zaduzen: izabrani?.id_zaduzen || null, napomena: "" })} />

                        {/* Potvrdu daje samo onaj kome je dokument dodeljen — server
                            to proverava nezavisno, ovo je da ostali ne vide dugme. */}
                        {izabrani && cekaOdluku && String(izabrani.id_zaduzen) === String(korisnikId) && (
                            <Button type="button" label={t("sefUlazne.potvrdiProveru")} icon="pi pi-verified"
                                className="p-button-sm" severity="success"
                                onClick={() => setProvera({ napomena: "" })} />
                        )}

                        <Button type="button" label={t("sefUlazne.prilozi")} icon="pi pi-paperclip"
                            className="p-button-sm p-button-outlined"
                            disabled={!izabrani} onClick={otvoriPriloge} />
                    </div>

                    {izabrani?.VecUneta === 1 && (
                        <div className="sef-ulazne-napomena sef-napomena-upozorenje">
                            <i className="pi pi-exclamation-triangle" />{" "}
                            {t("sefUlazne.vecUnetaOpis", { broj: izabrani.PostojecaFakturaBroj })}
                        </div>
                    )}

                    {!smeOdluku && (
                        <div className="sef-ulazne-napomena">
                            <i className="pi pi-lock" /> {t("sefUlazne.samoOdlucilac")}
                        </div>
                    )}
                </Card>
            </div>

            {/* ─────────── potvrda odluke ─────────── */}
            <Dialog header={naslovPotvrde} visible={!!odluka} style={{ width: "34rem" }}
                breakpoints={{ "768px": "95vw" }} onHide={() => { setOdluka(null); setRazlog(""); }}>
                {izabrani && (
                    <div className="sef-potvrda">
                        <p className="sef-potvrda-pitanje">
                            {odluka?.prihvacen
                                ? t("sefUlazne.pitanjePrihvati", {
                                    broj: izabrani.DocumentNumber,
                                    dobavljac: izabrani.SupplierName,
                                    iznos: `${iznos(izabrani.Amount)} ${izabrani.Currency || ""}`,
                                })
                                : t("sefUlazne.pitanjeOdbij", {
                                    broj: izabrani.DocumentNumber,
                                    dobavljac: izabrani.SupplierName,
                                    iznos: `${iznos(izabrani.Amount)} ${izabrani.Currency || ""}`,
                                })}
                        </p>
                        <p className="sef-potvrda-upozorenje">
                            <i className="pi pi-exclamation-triangle" /> {t("sefUlazne.nepovratno")}
                        </p>

                        <label htmlFor="razlogOdluke">
                            {odluka?.prihvacen ? t("sefUlazne.napomenaOpciono") : t("ulazne.razlogOdbijanja")}:
                        </label>
                        <InputTextarea id="razlogOdluke" rows={3} value={razlog}
                            style={{ width: "100%" }}
                            onChange={(e) => setRazlog(e.target.value)}
                            placeholder={odluka?.prihvacen ? "" : t("sefUlazne.phRazlog")} />

                        <div className="sef-potvrda-dugmad">
                            <Button type="button" label={t("common.cancel")} className="p-button-text"
                                onClick={() => { setOdluka(null); setRazlog(""); }} />
                            <Button type="button"
                                label={odluka?.prihvacen ? t("sefUlazne.prihvati") : t("sefUlazne.odbij")}
                                severity={odluka?.prihvacen ? "success" : "danger"}
                                /* Odbijanje bez razloga nije dozvoljeno ni ovde ni u proceduri —
                                   dugme to pokazuje umesto da korisnik klikne pa dobije prigovor. */
                                disabled={!odluka?.prihvacen && !razlog.trim()}
                                loading={salje} onClick={potvrdiOdluku} />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ─────────── PDF fakture sa SEF-a ─────────── */}
            <Dialog header={t("sefUlazne.dokumentNaslov")} visible={pdfDialog !== null}
                style={{ width: "80vw", height: "90vh" }} breakpoints={{ "768px": "95vw" }}
                contentStyle={{ display: "flex", flexDirection: "column", padding: 0 }}
                onHide={zatvoriDokument}>
                {pdfDialog && (
                    <>
                        <div className="sef-pdf-alat">
                            <a href={pdfDialog.url} download={pdfDialog.naziv}
                                className="p-button p-button-sm p-button-outlined">
                                <i className="pi pi-download" />&nbsp;{t("sefUlazne.preuzmiPdf")}
                            </a>
                        </div>
                        <iframe src={pdfDialog.url} title={t("sefUlazne.dokumentNaslov")}
                            className="sef-pdf-okvir" />
                    </>
                )}
            </Dialog>

            {/* ─────────── dodela na proveru ─────────── */}
            <Dialog header={t("sefUlazne.dodeliNaslov")} visible={!!dodela} style={{ width: "34rem" }}
                breakpoints={{ "768px": "95vw" }} onHide={() => setDodela(null)}>
                {dodela && (
                    <div className="sef-potvrda">
                        <p className="sef-potvrda-pitanje">{t("sefUlazne.dodeliOpis")}</p>

                        <label htmlFor="zaduzeni">{t("sefUlazne.operater")}:</label>
                        <CustomDropdown id="zaduzeni" value={dodela.id_zaduzen} optionLabel="label" filter
                            options={sifarnici.OperateriDB || []}
                            placeholder={t("sefUlazne.izaberiOperatera")}
                            onChange={(e) => setDodela((p) => ({ ...p, id_zaduzen: e.target.value }))} />

                        <label htmlFor="napomenaDodele">{t("sefUlazne.napomenaOpciono")}:</label>
                        <InputTextarea id="napomenaDodele" rows={3} value={dodela.napomena}
                            style={{ width: "100%" }}
                            onChange={(e) => setDodela((p) => ({ ...p, napomena: e.target.value }))} />

                        <div className="sef-potvrda-dugmad">
                            <Button type="button" label={t("common.cancel")} className="p-button-text"
                                onClick={() => setDodela(null)} />
                            <Button type="button" label={t("sefUlazne.dodeli")} icon="pi pi-send"
                                loading={salje} disabled={!dodela.id_zaduzen} onClick={potvrdiDodelu} />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ─────────── potvrda provere ─────────── */}
            <Dialog header={t("sefUlazne.potvrdiProveru")} visible={!!provera} style={{ width: "34rem" }}
                breakpoints={{ "768px": "95vw" }} onHide={() => setProvera(null)}>
                {provera && izabrani && (
                    <div className="sef-potvrda">
                        <p className="sef-potvrda-pitanje">
                            {t("sefUlazne.proveraOpis", {
                                broj: izabrani.DocumentNumber,
                                dobavljac: izabrani.SupplierName,
                            })}
                        </p>
                        <p className="sef-potvrda-upozorenje">
                            <i className="pi pi-exclamation-triangle" /> {t("sefUlazne.nepovratno")}
                        </p>

                        <label htmlFor="napomenaProvere">{t("sefUlazne.napomenaOpciono")}:</label>
                        <InputTextarea id="napomenaProvere" rows={3} value={provera.napomena}
                            style={{ width: "100%" }}
                            onChange={(e) => setProvera({ napomena: e.target.value })} />

                        <div className="sef-potvrda-dugmad">
                            <Button type="button" label={t("common.cancel")} className="p-button-text"
                                onClick={() => setProvera(null)} />
                            <Button type="button" label={t("sefUlazne.potvrdiProveru")} icon="pi pi-verified"
                                severity="success" loading={salje} onClick={potvrdiProveru} />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ─────────── prilozi uz fakturu ─────────── */}
            <Dialog header={t("sefUlazne.priloziNaslov")} visible={priloziDialog !== null}
                style={{ width: "44rem" }} breakpoints={{ "768px": "95vw" }}
                onHide={() => setPriloziDialog(null)}>
                {priloziDialog?.ucitava && (
                    <div className="sef-ulazne-napomena">
                        <i className="pi pi-spin pi-spinner" /> {t("common.ucitavanje")}
                    </div>
                )}
                {priloziDialog && !priloziDialog.ucitava && priloziDialog.spisak.length === 0 && (
                    <div className="sef-ulazne-napomena">
                        <i className="pi pi-info-circle" /> {t("sefUlazne.nemaPriloga")}
                    </div>
                )}
                {priloziDialog && !priloziDialog.ucitava && priloziDialog.spisak.map((p) => (
                    <div className="sef-prilog-red" key={p.redni}>
                        <i className="pi pi-file" />
                        <span className="sef-prilog-ime" title={p.naziv}>{p.naziv}</span>
                        <span className="sef-prilog-velicina">{p.velicinaKb} KB</span>
                        <Button type="button" icon="pi pi-eye" rounded outlined
                            className="p-button-sm" data-pr-tooltip={t("sefUlazne.otvoriPrilog")}
                            onClick={() => otvoriPrilog(p, false)} />
                        <Button type="button" icon="pi pi-download" rounded outlined
                            className="p-button-sm" data-pr-tooltip={t("sefUlazne.preuzmiPrilog")}
                            onClick={() => otvoriPrilog(p, true)} />
                    </div>
                ))}
            </Dialog>
        </div>
    );
};

export default PregledFaktura;
